import { GIVING_BASKETS, INACTIVE_BASKETS, PAYMENT_BASKETS, SUSPEND_BASKETS } from 'utils/constants';
import { Basket, EventStatus, PaginationHeaders } from 'types/common';

export type BasketsTabs = typeof GIVING_BASKETS | typeof PAYMENT_BASKETS | typeof SUSPEND_BASKETS | typeof INACTIVE_BASKETS;

/** Legacy UI status strings (still used in screens). */
export type EventStatuses = 'ACTIVE' | 'CLOSED' | 'FAILED' | 'REACHED' | 'DELETED' | 'PENDING' | 'SUSPEND';

/** /v1 BasketStatus. */
export type ApiBasketStatus = 'DRAFT' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';

export type ApiBasketType = 'GIVING' | 'PAYMENT';

/** Staff-visible basket row from GET /v1/parishes/:parishId/baskets. */
export interface BasketPublic {
    id: string;
    parishId: string;
    name: string;
    description: string | null;
    type: ApiBasketType;
    subType: string | null;
    goalAmountCents: number | null;
    currency: string;
    imageUrl: string | null;
    startDate: string | null;
    endDate: string | null;
    status: ApiBasketStatus;
    estimatedContributors: number | null;
    /**
     * System-owned basket (the per-parish "General Fund" — auto-created, exactly one per
     * parish). Always ACTIVE; the backend 409s suspend/close/delete/scheduled-status on it.
     *
     * Optional on the wire because it is absent until the backend ships the field, and
     * `isSystemBasket` treats absent as `false` — an ordinary basket, no affordances hidden.
     */
    isSystem?: boolean;
    /** Net collected (SUCCEEDED + PARTIALLY_REFUNDED, minus refunds). Integer cents. */
    collectedCents?: number;
    createdAt?: string;
    createdById?: string;
    /**
     * One pending-transition slot, plus an optional follow-up suspension that only has meaning
     * paired with a DRAFT's scheduled ACTIVE (activate at nextPlannedStatusAt, then suspend at
     * followUpSuspensionAt). Never derive scheduling from startDate.
     */
    nextPlannedStatus?: ApiBasketStatus | null;
    nextPlannedStatusAt?: string | null;
    followUpSuspensionAt?: string | null;
    updatedAt?: string;
}

/**
 * One row of GET /v1/parishes/:parishId/baskets/:basketId/contributions (CLERK+).
 * The backend returns only these fields — no donor name/username/status (see
 * BasketActivityContainer.tsx, which shows userId rather than inventing a display name).
 */
export interface BasketActivityUser {
    id: string;
    userId: string;
    amountCents: number;
    refundedCents: number;
    netCents: number;
    currency: string;
    createdAt: string;
}

export interface Baskets {
    currentTab: BasketsTabs;
}

export interface BasketState {
    loading: boolean;
    error: string | null;
    loadMore: boolean;
}

export interface GivingBasketState extends BasketState {
    activeRecords: Basket[];
    inActiveRecords: Basket[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- pre-existing
export interface PaymentBasketState extends GivingBasketState {}

export interface SuspendBasketState extends BasketState {
    suspendRecords: Basket[];
}

export interface InactiveBasketsState extends BasketState {
    records: Basket[];
}

export interface BasketHeaders {
    status: EventStatus;
    subType: string | undefined;
    name?: string;
}

export interface FetchGivingBasketPayload {
    status: EventStatus;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
    subType: any;
    headers: PaginationHeaders;
    name?: string;
    /** @deprecated Prefer parishId; kept for Freeze autocomplete callers. */
    companyId?: string;
    parishId?: string;
}

export interface GetEventListPayload {
    eventStatuses?: EventStatuses[];
    companyId: number;
    subType?: string;
    name?: string;
}
export interface UpdateBasketStatus {
    status: string;
    id: string | number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- pre-existing
export interface FetchPaymentBasketPayload extends FetchGivingBasketPayload {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- pre-existing
export interface FetchSuspendBasketPayload extends FetchGivingBasketPayload {}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- pre-existing
export interface FetchInactiveBasketPayload extends Omit<FetchGivingBasketPayload, 'subType' | 'status'> {}

/** Map legacy UI status → /v1 BasketStatus. */
export function toApiBasketStatus(status: string): ApiBasketStatus {
    if (status === 'SUSPEND') return 'SUSPENDED';
    if (status === 'PENDING') return 'DRAFT';
    return status as ApiBasketStatus;
}

/** Map /v1 status → legacy UI strings so existing screens keep working. */
export function toUiBasketStatus(status: string): EventStatuses | string {
    if (status === 'SUSPENDED') return 'SUSPEND';
    if (status === 'DRAFT') return 'PENDING';
    return status;
}

/** Display label for a UI-facing basket status (see toUiBasketStatus). PENDING is the backend's
 *  DRAFT — surfaced with the label staff actually see elsewhere (e.g. the Delete confirmation copy). */
export const BASKET_STATUS_LABELS: Record<string, string> = {
    PENDING: 'Draft',
    ACTIVE: 'Active',
    SUSPEND: 'Suspended',
    CLOSED: 'Closed',
};

export function toApiBasketType(subType?: string | null): ApiBasketType | undefined {
    if (subType === 'GIVING' || subType === 'PAYMENT') return subType;
    return undefined;
}

/**
 * Normalize a /v1 basket for existing list/detail/form consumers.
 * Aliases: imgUrl, subType (from type), PENDING/SUSPEND statuses.
 */
/**
 * System-owned basket — the per-parish "General Fund". The backend refuses to suspend, close,
 * delete or schedule a status transition on it, so the UI must not offer those affordances.
 *
 * Read the flag, never the name: `"General Fund"` is only the seeded display name and staff can
 * rename the basket. There must be no `name === 'General Fund'` check anywhere.
 *
 * Absent (backend has not shipped the field, or an older cached row) is treated as `false` — an
 * ordinary basket keeps every affordance it has today.
 */
export function isSystemBasket(basket: Pick<Basket, 'isSystem'> | null | undefined): boolean {
    return basket?.isSystem === true;
}

export function normalizeBasket(raw: BasketPublic | Record<string, unknown>): Basket {
    const b = raw as BasketPublic;
    const apiStatus = String(b.status || '');
    const goalCents = b.goalAmountCents ?? null;
    const collectedCents = typeof b.collectedCents === 'number' && Number.isFinite(b.collectedCents) ? b.collectedCents : 0;

    return {
        ...(b as unknown as Basket),
        id: b.id as unknown as number,
        name: b.name ?? '',
        description: b.description ?? '',
        status: toUiBasketStatus(apiStatus),
        type: b.type,
        subType: b.type || (b.subType as string) || undefined,
        imgUrl: b.imageUrl ?? null,
        imageUrl: b.imageUrl ?? null,
        goalAmount: goalCents != null ? goalCents / 100 : 0,
        goalAmountCents: goalCents,
        currencyCode: b.currency || 'USD',
        currency: b.currency || 'USD',
        startDate: b.startDate ?? '',
        endDate: b.endDate ?? '',
        // Read directly, exactly as the backend sends them — never aliased to the legacy
        // PENDING/SUSPEND UI strings. Consumers (CreateBasket, BasketDetailContainer) compare
        // against the real 'ACTIVE'/'SUSPENDED' values so there is no cross-vocabulary confusion.
        nextPlannedStatus: b.nextPlannedStatus ?? undefined,
        nextPlannedStatusAt: b.nextPlannedStatusAt || undefined,
        followUpSuspensionAt: b.followUpSuspensionAt || undefined,
        estimatedContributors: b.estimatedContributors ?? 0,
        collectedCents,
        balance: collectedCents / 100,
        parishId: b.parishId,
        isSystem: b.isSystem === true,
    } as Basket & { imageUrl?: string | null; goalAmountCents?: number | null; parishId?: string; currency?: string };
}

/** Build POST /v1 create body from the CreateBasket form values. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export function toCreateBasketPayload(form: Record<string, any>) {
    const type = toApiBasketType(form.subType) || 'GIVING';
    const payload: Record<string, unknown> = {
        name: String(form.name || '').trim(),
        type,
        currency: 'USD',
    };
    if (form.description != null && String(form.description).trim()) {
        payload.description = String(form.description).trim();
    }
    const goal = Number(form.goalAmount);
    if (Number.isFinite(goal) && goal > 0) {
        payload.goalAmountCents = Math.round(goal * 100);
    }
    if (form.estimatedContributors != null && form.estimatedContributors !== '') {
        payload.estimatedContributors = Number(form.estimatedContributors);
    }
    if (form.startDate) {
        payload.startDate = form.startDate;
    }
    if (form.endDate) {
        payload.endDate = form.endDate;
    }
    // Scheduled activation while still DRAFT, optionally with a follow-up suspension — one
    // request, never create-then-PATCH. A brand-new basket can never schedule SUSPENDED directly
    // (there is nothing to suspend yet), so this is the only schedule shape Create ever sends.
    if (form.nextPlannedStatus === 'ACTIVE' && form.nextPlannedStatusAt) {
        payload.nextPlannedStatus = 'ACTIVE';
        payload.nextPlannedStatusAt = form.nextPlannedStatusAt;
        if (form.followUpSuspensionAt) {
            payload.followUpSuspensionAt = form.followUpSuspensionAt;
        }
    }
    return payload;
}

/** Build PATCH /v1 update body (no type/subType/currency/status). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing
export function toUpdateBasketPayload(form: Record<string, any>) {
    const payload: Record<string, unknown> = {};
    if (form.name != null) payload.name = String(form.name).trim();
    if (form.description !== undefined) {
        const d = form.description == null ? null : String(form.description).trim();
        payload.description = d || null;
    }
    if (form.goalAmount != null && form.goalAmount !== '') {
        const goal = Number(form.goalAmount);
        payload.goalAmountCents = Number.isFinite(goal) && goal > 0 ? Math.round(goal * 100) : null;
    }
    if (form.estimatedContributors !== undefined) {
        payload.estimatedContributors =
            form.estimatedContributors == null || form.estimatedContributors === '' ? null : Number(form.estimatedContributors);
    }
    if (form.startDate !== undefined) payload.startDate = form.startDate || null;
    if (form.endDate !== undefined) payload.endDate = form.endDate || null;

    // Each schedule key is only included when the caller explicitly put it in `form` — the
    // caller (CreateBasket) is responsible for diffing against the originally-hydrated schedule
    // and omitting keys that did not change, per the PATCH contract: omitted = unchanged,
    // null = explicitly clear. nextPlannedStatus/nextPlannedStatusAt are sent as a pair whenever
    // either changes (never one without the other), which the caller also guarantees.
    if (form.nextPlannedStatus !== undefined) {
        payload.nextPlannedStatus = form.nextPlannedStatus;
    }
    if (form.nextPlannedStatusAt !== undefined) {
        payload.nextPlannedStatusAt = form.nextPlannedStatusAt || null;
    }
    if (form.followUpSuspensionAt !== undefined) {
        payload.followUpSuspensionAt = form.followUpSuspensionAt || null;
    }
    return payload;
}
